import React, { useState, useMemo, useEffect } from "react";
import ReactDOM from "react-dom";
import Swal from "sweetalert2"; // Aseguramos que Swal esté importado para las alertas

export const ESTADOS_EVALUACION = {
  FAVORABLE: { min: 90, etiqueta: "Favorable", clase: "bg-emerald-100 text-emerald-800 border-emerald-300", colorBadge: "emerald", rangoTexto: "90% - 100%", clasesKPI: { activo: "bg-emerald-100 border-emerald-500 ring-2 ring-emerald-400", inactivo: "bg-emerald-50 border-emerald-200 hover:bg-emerald-100/70", textoTitulo: "text-emerald-800", textoNumero: "text-emerald-700" } },
  COMPROMISOS: { min: 70, etiqueta: "Con compromisos", clase: "bg-amber-100 text-amber-800 border-amber-300", colorBadge: "amber", rangoTexto: "70% - 89%", clasesKPI: { activo: "bg-amber-100 border-amber-500 ring-2 ring-amber-400", inactivo: "bg-amber-50 border-amber-200 hover:bg-amber-100/70", textoTitulo: "text-amber-800", textoNumero: "text-amber-700" } },
  SEGUIMIENTO: { min: 60, etiqueta: "Seguimiento", clase: "bg-orange-100 text-orange-800 border-orange-300", colorBadge: "orange", rangoTexto: "60% - 69%", clasesKPI: { activo: "bg-orange-100 border-orange-500 ring-2 ring-orange-400", inactivo: "bg-orange-50 border-orange-200 hover:bg-orange-100/70", textoTitulo: "text-orange-800", textoNumero: "text-orange-700" } },
  NO_APTO: { min: 0, etiqueta: "No apto", clase: "bg-red-100 text-red-800 border-red-300", colorBadge: "red", rangoTexto: "< 60%", clasesKPI: { activo: "bg-red-100 border-red-500 ring-2 ring-red-400", inactivo: "bg-red-50 border-red-200 hover:bg-red-100/70", textoTitulo: "text-red-800", textoNumero: "text-red-700" } }
};

export const obtenerEstadoEvaluacion = (puntaje) => {
  const num = Number(puntaje || 0);
  if (num >= 90) return { ...ESTADOS_EVALUACION.FAVORABLE, clave: "FAVORABLE" };
  if (num >= 70) return { ...ESTADOS_EVALUACION.COMPROMISOS, clave: "COMPROMISOS" };
  if (num >= 60) return { ...ESTADOS_EVALUACION.SEGUIMIENTO, clave: "SEGUIMIENTO" };
  return { ...ESTADOS_EVALUACION.NO_APTO, clave: "NO_APTO" };
};

const calcularPuntajePorcentaje = (item) => {
  if (item?.puntajeCalculado !== undefined) return Number(item.puntajeCalculado);
  const rawPuntaje = Number(item?.puntaje);
  if (rawPuntaje > 4) return rawPuntaje;
  const respuestas = item?.evaluacion_respuestas || item?.preguntas || item?.respuestas || item?.detalles || item?.criterios;
  if (respuestas) {
    const lista = Array.isArray(respuestas) ? respuestas : Object.values(respuestas);
    if (lista.length > 0) {
      let sumaObtenida = 0; let sumaMaxima = lista.length * 4;
      lista.forEach((r) => { const val = typeof r === "object" ? Number(r.valor ?? r.puntaje ?? r.puntos ?? 0) : Number(r); sumaObtenida += isNaN(val) ? 0 : val; });
      if (sumaMaxima > 0) return Number(((sumaObtenida / sumaMaxima) * 100).toFixed(2));
    }
  }
  if (rawPuntaje > 0 && rawPuntaje <= 4) return Number(((rawPuntaje / 4) * 100).toFixed(2));
  return rawPuntaje || 0;
};

const obtenerEstadoVigencia = (fechaSeguimiento) => {
  if (!fechaSeguimiento) return "SIN_FECHA";
  
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  
  const fechaSeg = new Date(fechaSeguimiento);
  fechaSeg.setHours(0, 0, 0, 0);
  
  const diffTime = fechaSeg.getTime() - hoy.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return "VENCIDOS"; 
  if (diffDays <= 30) return "POR_VENCER"; 
  return "VIGENTES"; 
};

export default function EvaluacionesDashboard({ evaluaciones: propEvaluaciones = [], usuario: propUsuario, onStartNewEvaluation, onBackToMenu }) {
  const [busqueda, setBusqueda] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("TODOS");
  const [filtroVigencia, setFiltroVigencia] = useState("TODOS"); 
  const [sedeFiltroAdmin, setSedeFiltroAdmin] = useState("TODAS"); 
  const [evaluacionSeleccionada, setEvaluacionSeleccionada] = useState(null);
  const [mostrarFirmas, setMostrarFirmas] = useState(false);
  const [listaEvaluaciones, setListaEvaluaciones] = useState(propEvaluaciones);
  const [enviandoAlertas, setEnviandoAlertas] = useState(false); 

  const usuario = useMemo(() => {
    if (propUsuario && Object.keys(propUsuario).length > 0) return propUsuario;
    try { const userStr = localStorage.getItem("usuario") || localStorage.getItem("user"); if (userStr) return JSON.parse(userStr); } catch (e) {}
    return {};
  }, [propUsuario]);

  useEffect(() => {
    const verificarPermisosYCargarDatos = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL;
        const headers = { "Content-Type": "application/json" };
        if (usuario) {
          headers["x-user-data"] = JSON.stringify(usuario);
          headers["x-abac-permissions"] = typeof usuario.permisos_avanzados === 'object' ? JSON.stringify(usuario.permisos_avanzados) : (usuario.permisos_avanzados || "{}");
        }
        
        const urlFetch = `${API_URL}/api/evaluaciones`; 
        const response = await fetch(urlFetch, { headers });
        const data = await response.json();
        
        if (data.success && Array.isArray(data.evaluaciones)) {
          setListaEvaluaciones(data.evaluaciones);
        }
      } catch (err) { console.error("Error validando permisos y evaluaciones:", err); }
    };
    verificarPermisosYCargarDatos();
  }, [usuario]);

  useEffect(() => {
    if (evaluacionSeleccionada) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "auto";
    return () => { document.body.style.overflow = "auto"; };
  }, [evaluacionSeleccionada]);

  const permisosABAC = typeof usuario?.permisos_avanzados === 'string' ? JSON.parse(usuario.permisos_avanzados || '{}') : (usuario?.permisos_avanzados || {});
  const permisosEval = permisosABAC.micros ? permisosABAC.micros["Formulario de Evaluación"] : permisosABAC["Formulario de Evaluación"];
  
  const esSuperAdmin = String(usuario?.cargo || "").toUpperCase().includes("SUPER");
  const tieneAccesoGlobal = esSuperAdmin || permisosEval?.alcance === "GLOBAL";
  const tienePermisoModulo = esSuperAdmin || permisosEval !== undefined;

  const sedesDisponibles = useMemo(() => {
    if (!Array.isArray(listaEvaluaciones)) return [];
    const sedesSet = new Set();
    listaEvaluaciones.forEach(ev => { const s = ev?.centroTrabajo || ev?.centro_trabajo; if (s) sedesSet.add(String(s).trim().toUpperCase()); });
    return Array.from(sedesSet);
  }, [listaEvaluaciones]);

  const evaluacionesPermitidas = useMemo(() => {
    if (!Array.isArray(listaEvaluaciones)) return [];
    if (tieneAccesoGlobal && sedeFiltroAdmin !== "TODAS") {
      return listaEvaluaciones.filter(ev => String(ev?.centroTrabajo || ev?.centro_trabajo || "").trim().toUpperCase() === sedeFiltroAdmin);
    }
    return listaEvaluaciones; 
  }, [listaEvaluaciones, tieneAccesoGlobal, sedeFiltroAdmin]);

  const ultimasEvaluaciones = useMemo(() => {
    const mapa = new Map();
    evaluacionesPermitidas.forEach((ev) => {
      const key = String(ev?.documento || ev?.empleado || ev?.nombre || "").toLowerCase().trim();
      if (!key) return;
      const fechaValida = (val) => { if (!val) return 0; const t = new Date(val).getTime(); return isNaN(t) ? 0 : t; };
      if (!mapa.has(key)) { mapa.set(key, ev); } else {
        const existente = mapa.get(key);
        const fechaActual = fechaValida(ev?.fechaEvaluacion || ev?.fecha_evaluacion || ev?.fecha) || Number(ev?.id) || 0;
        const fechaExistente = fechaValida(existente?.fechaEvaluacion || existente?.fecha_evaluacion || existente?.fecha) || Number(existente?.id) || 0;
        if (fechaActual >= fechaExistente) mapa.set(key, ev);
      }
    });
    return Array.from(mapa.values());
  }, [evaluacionesPermitidas]);

  const estadisticas = useMemo(() => {
    const conteos = { FAVORABLE: 0, COMPROMISOS: 0, SEGUIMIENTO: 0, NO_APTO: 0, total: ultimasEvaluaciones.length };
    ultimasEvaluaciones.forEach((ev) => { const estado = obtenerEstadoEvaluacion(calcularPuntajePorcentaje(ev)); if (conteos[estado.clave] !== undefined) conteos[estado.clave]++; });
    return conteos;
  }, [ultimasEvaluaciones]);

  const evaluacionesFiltradas = useMemo(() => {
    return ultimasEvaluaciones.filter((ev) => {
      const empleado = String(ev?.empleado || ev?.nombre || "").toLowerCase(); const cargo = String(ev?.cargo || "").toLowerCase(); const doc = String(ev?.documento || "").toLowerCase(); const termino = busqueda.trim().toLowerCase();
      const coincideBusqueda = !termino || empleado.includes(termino) || cargo.includes(termino) || doc.includes(termino);
      
      const estadoItem = obtenerEstadoEvaluacion(calcularPuntajePorcentaje(ev));
      const coincideCategoria = filtroCategoria === "TODOS" || estadoItem.clave === filtroCategoria;
      
      const vigenciaItem = obtenerEstadoVigencia(ev?.fecha_seguimiento || ev?.fechaSeguimiento);
      const coincideVigencia = filtroVigencia === "TODOS" || vigenciaItem === filtroVigencia;

      return coincideBusqueda && coincideCategoria && coincideVigencia;
    });
  }, [ultimasEvaluaciones, busqueda, filtroCategoria, filtroVigencia]);

  const formatearFecha = (fechaStr) => {
    if (!fechaStr) return "N/A";
    const fecha = new Date(fechaStr);
    fecha.setMinutes(fecha.getMinutes() + fecha.getTimezoneOffset());
    return fecha.toLocaleDateString('es-CO', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  //Enviar notificaciones a vencidos
  const handleAlertarVencidos = async () => {
    const personalParaAlertar = ultimasEvaluaciones.filter((ev) => {
      const vigencia = obtenerEstadoVigencia(ev?.fecha_seguimiento || ev?.fechaSeguimiento);
      return vigencia === "POR_VENCER" || vigencia === "VENCIDOS";
    });

    if (personalParaAlertar.length === 0) {
      return Swal.fire({
        icon: 'info',
        title: '¡Todo al día!',
        text: 'No hay personal próximo a vencer o con evaluaciones vencidas.',
        confirmButtonColor: '#3b82f6'
      });
    }

    const confirm = await Swal.fire({
      title: `¿Alertar a ${personalParaAlertar.length} colaboradores?`,
      text: "Se enviará una notificación a sus teléfonos para recordarles de su evaluación.",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#64748b',
      confirmButtonText: '🔔 Sí, enviar alertas',
      cancelButtonText: 'Cancelar'
    });

    if (!confirm.isConfirmed) return;

    setEnviandoAlertas(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL;
      let enviados = 0;

      // Enviamos la notificación uno por uno (o a quienes tengan la app instalada)
      for (const emp of personalParaAlertar) {
        if (!emp.documento) continue;

        await fetch(`${API_URL}/api/notificaciones/enviar-manual`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            destinatario: emp.documento,
            titulo: 'Evaluación de Desempeño ⚠️',
            mensaje: `Hola ${emp.nombre || emp.empleado}, tu evaluación de desempeño está próxima a vencer o requiere seguimiento. Por favor comunícate con Talento Humano.`,
            tipo: 'INDIVIDUAL'
          })
        });
        enviados++;
      }

      Swal.fire({
        icon: 'success',
        title: 'Alertas Enviadas',
        text: `Se dispararon notificaciones exitosamente a ${enviados} colaboradores.`,
        confirmButtonColor: '#10b981'
      });

    } catch (error) {
      console.error("Error enviando notificaciones:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error de Red',
        text: 'Hubo un problema de conexión al intentar enviar las alertas.',
        confirmButtonColor: '#ef4444'
      });
    } finally {
      setEnviandoAlertas(false);
    }
  };

  const renderModalDetalle = () => {
    if (!evaluacionSeleccionada) return null;
    return ReactDOM.createPortal(
      <div className="fixed top-0 left-0 w-screen h-screen bg-slate-900/40 backdrop-blur-md flex justify-center items-center p-2 sm:p-4 z-[99999]">
        <div className="w-full max-w-[95vw] lg:max-w-[90vw] xl:max-w-7xl max-h-[95vh] lg:max-h-[90vh] bg-white/40 backdrop-blur-2xl rounded-3xl shadow-[0_8px_32px_0_rgba(31,38,135,0.2)] border border-white/60 flex flex-col relative overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200/60 bg-white/60 backdrop-blur-md sticky top-0 z-20 flex flex-col gap-1.5">
            <button type="button" onClick={() => { setEvaluacionSeleccionada(null); setMostrarFirmas(false); }} className="absolute right-4 top-4 bg-white/60 hover:bg-white/90 text-slate-800 font-bold w-7 h-7 rounded-full flex items-center justify-center transition shadow-sm border border-white/80 cursor-pointer text-[9px]">✕</button>
            <div><span className="inline-block text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-blue-800 bg-white/60 border border-white/80 px-2.5 py-1 rounded-md shadow-sm">Detalle Evaluación</span></div>
            <h3 className="text-sm sm:text-xl lg:text-2xl font-black text-slate-800 uppercase tracking-tight leading-snug break-words pr-10">{evaluacionSeleccionada.empleado || evaluacionSeleccionada.nombre}</h3>
          </div>
          <div className="flex flex-col lg:flex-row flex-1 overflow-y-auto lg:overflow-hidden p-3 sm:p-5 gap-4 custom-scrollbar">
            <div className="w-full lg:w-[40%] flex flex-col gap-3 h-fit lg:h-auto lg:overflow-y-auto pr-1 sm:pr-2 custom-scrollbar shrink-0">
              
              <div className="bg-white/50 border border-white/60 rounded-2xl p-4 sm:p-5 shadow-sm backdrop-blur-md">
                <div className="grid grid-cols-2 gap-y-4 gap-x-2 text-xs sm:text-sm">
                  <div><p className="text-[9px] sm:text-[10px] font-black uppercase text-slate-500 mb-0.5">Documento</p><p className="font-bold text-slate-800">{evaluacionSeleccionada.documento || "N/A"}</p></div>
                  <div><p className="text-[9px] sm:text-[10px] font-black uppercase text-slate-500 mb-0.5">Cargo</p><p className="font-bold text-slate-800 break-words pr-2">{evaluacionSeleccionada.cargo || "N/A"}</p></div>
                  <div><p className="text-[9px] sm:text-[10px] font-black uppercase text-slate-500 mb-0.5">Sede</p><p className="font-bold text-slate-800 break-words pr-2">{evaluacionSeleccionada.centroTrabajo || evaluacionSeleccionada.centro_trabajo || "N/A"}</p></div>
                  <div><p className="text-[9px] sm:text-[10px] font-black uppercase text-slate-500 mb-0.5">Evaluador</p><p className="font-bold text-slate-800 break-words pr-2">{evaluacionSeleccionada.evaluador || "N/A"}</p></div>
                  <div><p className="text-[9px] sm:text-[10px] font-black uppercase text-slate-500 mb-0.5">Fecha Evaluación</p><p className="font-bold text-slate-800">{formatearFecha(evaluacionSeleccionada.fecha_evaluacion || evaluacionSeleccionada.fechaEvaluacion)}</p></div>
                  <div><p className="text-[9px] sm:text-[10px] font-black uppercase text-slate-500 mb-0.5">Próx. Seguimiento</p><p className="font-bold text-slate-800">{formatearFecha(evaluacionSeleccionada.fecha_seguimiento || evaluacionSeleccionada.fechaSeguimiento)}</p></div>
                </div>
              </div>

              <div className="bg-white/50 border border-white/60 rounded-2xl p-4 sm:p-5 shadow-sm backdrop-blur-md flex items-center justify-between">
                <div><p className="text-[10px] sm:text-xs font-black uppercase text-slate-500">Puntaje Final</p><p className="text-3xl sm:text-4xl font-black text-blue-800 drop-shadow-sm leading-none mt-1">{calcularPuntajePorcentaje(evaluacionSeleccionada)}%</p></div>
                <div className="text-right"><p className="text-[9px] sm:text-[10px] font-black uppercase text-slate-500">Estado / Concepto</p><p className={`text-xs sm:text-sm font-black px-3 py-1.5 rounded-lg mt-1.5 inline-block border ${ calcularPuntajePorcentaje(evaluacionSeleccionada) >= 60 ? "bg-emerald-100/70 text-emerald-900 border-emerald-300/60" : "bg-red-100/70 text-red-900 border-red-300/60" }`}>{evaluacionSeleccionada.concepto || evaluacionSeleccionada.concepto_final || evaluacionSeleccionada.conceptoFinal || "Eval. Finalizada"}</p></div>
              </div>

              <div className="bg-white/50 border border-white/60 rounded-2xl p-4 sm:p-5 shadow-sm backdrop-blur-md flex-1">
                <h4 className="text-[10px] sm:text-xs font-black uppercase text-slate-500 mb-1.5">Fortalezas</h4><p className="text-xs sm:text-sm text-slate-700 font-medium mb-4 italic">{evaluacionSeleccionada.fortalezas || evaluacionSeleccionada.observaciones_fortalezas || "Sin observaciones registradas."}</p>
                <h4 className="text-[10px] sm:text-xs font-black uppercase text-slate-500 mb-1.5">Aspectos a Mejorar</h4><p className="text-xs sm:text-sm text-slate-700 font-medium italic">{evaluacionSeleccionada.aspectosMejora || evaluacionSeleccionada.aspectos_mejora || "Sin observaciones registradas."}</p>
              </div>

              {evaluacionSeleccionada.firmas && evaluacionSeleccionada.firmas.length > 0 && (
                <div className="bg-white/50 border border-white/60 rounded-2xl p-3 sm:p-4 shadow-sm backdrop-blur-md">
                  <button type="button" onClick={() => setMostrarFirmas(!mostrarFirmas)} className="w-full flex items-center justify-center gap-2 text-blue-900 font-bold text-xs sm:text-sm bg-white/60 hover:bg-white/80 border border-white/80 p-2.5 sm:p-3 rounded-xl transition cursor-pointer"><span>✍️ {mostrarFirmas ? "Ocultar Firmas" : "Ver Firmas"}</span></button>
                  {mostrarFirmas && (<div className="flex gap-3 mt-3">{evaluacionSeleccionada.firmas.map((firmaObj, idx) => (<div key={idx} className="flex-1 bg-white/60 rounded-xl p-2 sm:p-3 text-center border border-white/80"><p className="text-[9px] sm:text-[10px] uppercase font-bold text-slate-600 mb-1.5">{firmaObj.tipo_firma}</p><img src={firmaObj.firma} alt="firma" className="h-10 sm:h-12 object-contain mx-auto mix-blend-multiply opacity-80" /></div>))}</div>)}
                </div>
              )}
            </div>
            <div className="w-full lg:w-[60%] bg-white/40 border border-white/60 rounded-2xl p-3 sm:p-5 shadow-sm backdrop-blur-md flex flex-col h-fit lg:h-auto lg:overflow-hidden">
              <div className="flex justify-between items-center border-b border-white/50 pb-2 mb-3 shrink-0"><h4 className="text-xs sm:text-sm font-black uppercase text-slate-800">Puntaje por Criterio</h4><span className="text-[10px] text-slate-600 font-bold">Escala 1 a 4</span></div>
              <div className="flex-1 lg:overflow-y-auto pr-1 sm:pr-2 custom-scrollbar">
                {(() => {
                  const itemsDesglose = evaluacionSeleccionada.evaluacion_respuestas || evaluacionSeleccionada.preguntas || evaluacionSeleccionada.respuestas || evaluacionSeleccionada.detalles || evaluacionSeleccionada.criterios;
                  if (!itemsDesglose || (Array.isArray(itemsDesglose) && itemsDesglose.length === 0)) return <p className="text-xs text-slate-600 italic">No hay información detallada.</p>;
                  const lista = Array.isArray(itemsDesglose) ? itemsDesglose.map((item, idx) => ({ titulo: item.pregunta_key || item.pregunta || item.criterio || item.nombre || `Criterio #${idx + 1}`, puntos: item.valor ?? item.puntaje ?? item.puntos ?? item, seccion: item.seccion || "" })) : Object.entries(itemsDesglose).map(([llave, val]) => ({ titulo: typeof val === "object" ? (val.pregunta_key || val.nombre || val.pregunta || llave) : llave, puntos: typeof val === "object" ? (val.valor ?? val.puntaje ?? val.puntos) : val, seccion: typeof val === "object" ? val.seccion : "" }));
                  return (<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 pb-2">{lista.map((elem, i) => (<div key={i} className="bg-white/70 border border-white/80 p-2 sm:p-3 rounded-xl flex items-center justify-between shadow-sm hover:bg-white/90 transition"><span className="text-[10px] sm:text-xs font-semibold text-slate-800 truncate pr-2 w-[75%] leading-tight">{elem.seccion && <span className="text-slate-600 font-black mr-1.5">[{elem.seccion}]</span>}{String(elem.titulo).replace(/_/g, " ")}</span><span className="text-[10px] font-black text-blue-900 bg-white px-2 py-1 rounded-md border border-white whitespace-nowrap shadow-sm">{Number(elem.puntos) || 0} / 4</span></div>))}</div>);
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  if (!tienePermisoModulo) {
    return (
      <div className="w-full max-w-4xl bg-white/90 backdrop-blur-xl rounded-3xl shadow-xl border border-white/50 my-auto p-12 text-center relative animate-fade-in mx-auto">
        <span className="text-7xl block mb-6 drop-shadow-md">🔒</span>
        <h2 className="text-3xl font-black text-slate-800 mb-3 tracking-tight">Acceso Denegado</h2>
        <p className="text-base text-slate-600 mb-8 font-medium max-w-lg mx-auto">
          El módulo de Evaluación está habilitado en tu panel, pero no cuentas con los <b>privilegios internos (Submódulos)</b> para ver o gestionar esta información. Contacta a Talento Humano.
        </p>
        <button onClick={onBackToMenu} className="px-8 py-3.5 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-bold rounded-xl transition-all shadow-[0_8px_20px_rgba(37,99,235,0.3)] cursor-pointer">
          Volver al Panel Principal
        </button>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-5 relative">
      <div className="border-b border-slate-200 pb-4 flex flex-col sm:flex-row justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Módulo de Evaluación de Desempeño</h2>
          <p className="text-slate-500 text-xs sm:text-sm mt-0.5">
            {tieneAccesoGlobal ? (sedeFiltroAdmin === "TODAS" ? "Vista global corporativa: Todas las sedes" : `Filtrando corporativo por sede: ${sedeFiltroAdmin}`) : `Gestionando el personal de tu sede: ${usuario?.centroTrabajo || usuario?.centro_trabajo || "Sin asignar"}`}
          </p>
        </div>
        
        {/* BOTONES AGRUPADOS DE NUEVA EVALUACIÓN Y ALERTAS */}
        <div className="flex gap-2">
          {tieneAccesoGlobal && (
            <button 
              type="button" 
              onClick={handleAlertarVencidos} 
              disabled={enviandoAlertas}
              className="bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white font-bold py-2.5 px-4 rounded-xl text-xs sm:text-sm transition shadow-sm cursor-pointer whitespace-nowrap text-center disabled:opacity-50"
            >
              {enviandoAlertas ? "⏳ Enviando..." : "🔔 Alertar Vencidos"}
            </button>
          )}
          <button type="button" onClick={() => onStartNewEvaluation?.()} className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold py-2.5 px-4 rounded-xl text-xs sm:text-sm transition shadow-sm cursor-pointer whitespace-nowrap text-center">
            + Nueva Evaluación
          </button>
        </div>
      </div>

      {tieneAccesoGlobal && (
        <div className="bg-slate-100/80 border border-slate-200 p-3 rounded-xl flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-xs sm:text-sm font-bold text-slate-700">🏢 Filtrar vista por Sede:</span>
          <select value={sedeFiltroAdmin} onChange={(e) => setSedeFiltroAdmin(e.target.value)} className="w-full sm:w-auto border border-slate-300 rounded-xl px-4 py-2 text-xs sm:text-sm bg-white font-semibold text-slate-800 outline-none focus:ring-2 focus:ring-blue-600">
            <option value="TODAS">🌟 Ver Todas las Sedes (Corporativo)</option>
            {sedesDisponibles.map((sede) => (<option key={sede} value={sede}>Sede: {sede}</option>))}
          </select>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[ { clave: "FAVORABLE", config: ESTADOS_EVALUACION.FAVORABLE, total: estadisticas.FAVORABLE }, { clave: "COMPROMISOS", config: ESTADOS_EVALUACION.COMPROMISOS, total: estadisticas.COMPROMISOS }, { clave: "SEGUIMIENTO", config: ESTADOS_EVALUACION.SEGUIMIENTO, total: estadisticas.SEGUIMIENTO }, { clave: "NO_APTO", config: ESTADOS_EVALUACION.NO_APTO, total: estadisticas.NO_APTO }
        ].map(({ clave, config, total }) => {
          const estaActivo = filtroCategoria === clave;
          return (
            <div key={clave} onClick={() => setFiltroCategoria(estaActivo ? "TODOS" : clave)} className={`border rounded-xl p-3 sm:p-4 cursor-pointer transition select-none ${estaActivo ? config.clasesKPI.activo : config.clasesKPI.inactivo}`}>
              <p className={`text-[11px] font-semibold uppercase tracking-wider ${config.clasesKPI.textoTitulo}`}>{config.etiqueta} ({config.rangoTexto})</p>
              <p className={`text-xl sm:text-2xl font-bold mt-1 ${config.clasesKPI.textoNumero}`}>{total}</p>
            </div>
          );
        })}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <input type="text" placeholder="Buscar por documento, empleado o cargo..." value={busqueda} onChange={(e) => setBusqueda(e.target.value)} className="w-full sm:flex-1 border border-slate-300 rounded-xl px-4 py-2.5 text-xs sm:text-sm outline-none focus:ring-2 focus:ring-blue-600 bg-white" />
        
        <select value={filtroVigencia} onChange={(e) => setFiltroVigencia(e.target.value)} className="w-full sm:w-auto border border-slate-300 rounded-xl px-4 py-2.5 text-xs sm:text-sm bg-white font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-blue-600 cursor-pointer">
          <option value="TODOS">📅 Todas las fechas</option>
          <option value="VIGENTES">✅ Al día (Vigentes)</option>
          <option value="POR_VENCER">⚠️ Próximos a vencer (30 días)</option>
          <option value="VENCIDOS">🚨 Vencidos (Re-evaluar)</option>
        </select>
        
        <button type="button" onClick={() => onBackToMenu?.()} className="w-full sm:w-40 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-4 rounded-xl text-xs sm:text-sm transition shadow-sm cursor-pointer text-center">Volver al menú</button>
      </div>

      <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white shadow-sm">
        <table className="w-full text-left text-xs sm:text-sm text-slate-700 border-collapse">
          <thead className="bg-slate-50 text-slate-800 font-bold border-b border-slate-200">
            <tr>
              <th className="py-3.5 px-4">Empleado</th>
              <th className="py-3.5 px-4">Cargo</th>
              {(tieneAccesoGlobal || sedeFiltroAdmin === "TODAS") && <th className="py-3.5 px-4">Sede</th>}
              <th className="py-3.5 px-4 text-center">Puntaje</th>
              <th className="py-3.5 px-4 text-center">Estado</th>
              <th className="py-3.5 px-4 text-center">Vigencia</th>
              <th className="py-3.5 px-4 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {evaluacionesFiltradas.length > 0 ? (
              evaluacionesFiltradas.map((item, index) => {
                const porcentaje = calcularPuntajePorcentaje(item);
                const estadoInfo = obtenerEstadoEvaluacion(porcentaje);
                
                const vigencia = obtenerEstadoVigencia(item?.fecha_seguimiento || item?.fechaSeguimiento);
                let badgeVigencia = null;
                if (vigencia === "VIGENTES") badgeVigencia = <span className="inline-block whitespace-nowrap bg-emerald-100 text-emerald-800 border border-emerald-300 px-2.5 py-1.5 rounded-lg text-[10px] font-bold">✅ Al día</span>;
                else if (vigencia === "POR_VENCER") badgeVigencia = <span className="inline-block whitespace-nowrap bg-amber-100 text-amber-800 border border-amber-300 px-2.5 py-1.5 rounded-lg text-[10px] font-bold">⚠️ Por vencer</span>;
                else if (vigencia === "VENCIDOS") badgeVigencia = <span className="inline-block whitespace-nowrap bg-red-100 text-red-800 border border-red-300 px-2.5 py-1.5 rounded-lg text-[10px] font-bold">🚨 Vencido</span>;
                else badgeVigencia = <span className="inline-block whitespace-nowrap bg-slate-100 text-slate-600 border border-slate-300 px-2.5 py-1.5 rounded-lg text-[10px] font-bold">Sin fecha</span>;

                return (
                  <tr key={item?.id || index} className="hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-4 font-medium text-slate-800">{item?.empleado || item?.nombre || "N/A"}</td>
                    <td className="py-3 px-4 text-slate-500 uppercase">{item?.cargo || "N/A"}</td>
                    {(tieneAccesoGlobal || sedeFiltroAdmin === "TODAS") && <td className="py-3 px-4 text-slate-500 uppercase">{item?.centroTrabajo || item?.centro_trabajo || "N/A"}</td>}
                    <td className="py-3 px-4 font-bold text-slate-800 text-center">{porcentaje}%</td>
                    <td className="py-3 px-4 text-center"><span className={`inline-block whitespace-nowrap px-2.5 py-1 rounded-md text-[11px] font-bold border ${estadoInfo.clase}`}>{estadoInfo.etiqueta}</span></td>
                    <td className="py-3 px-4 text-center">{badgeVigencia}</td>
                    <td className="py-3 px-4 text-right"><button type="button" onClick={() => { setEvaluacionSeleccionada(item); setMostrarFirmas(false); }} className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium py-1.5 px-3 rounded-lg text-xs transition cursor-pointer">Ver detalle</button></td>
                  </tr>
                );
              })
            ) : (
              <tr><td colSpan={tieneAccesoGlobal ? 7 : 6} className="py-10 text-center text-slate-400 italic text-xs">No hay evaluaciones registradas con los filtros actuales.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {renderModalDetalle()}
    </div>
  );
}